import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useId,
  useLayoutEffect,
  type ComponentRef,
} from "react";
import { createPortal } from "react-dom";
import type { ViewStyle } from "react-native";

import { Box, Text, type BoxRef } from "../../../mads";
import { SelectInput } from "../../../mads/SelectInput";
import { SelectList } from "../../../mads/SelectList";
import { useTheme } from "../../../mads/ThemeProvider";
import { uninertOwnPortal } from "../../../shared/util/useWebModalAccessibility";

// Elements that participate in the keyboard tab order. Used to advance focus to
// the next/previous field when the open list is dismissed with Tab.
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const visuallyHiddenValueStyle: ViewStyle = {
  position: "absolute",
  opacity: 0.00000001,
  overflow: "hidden",
  pointerEvents: "none",
  height: 1,
  width: 1,
};

export type SelectItem<T> = {
  label: string;
  value: T;
};

export type SelectProps<ValueT> = {
  /** The currently selected value */
  value?: ValueT;
  /** Callback when a value is selected */
  onChange: (value: ValueT) => void;
  /** The list of items to display in the dropdown */
  items: SelectItem<ValueT>[];
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Label text for the input */
  labelText?: string;
  /**
   * Accessible name for the trigger when its visible label lives outside this
   * component.
   */
  ariaLabel?: string;
  /** Helper text shown below the label */
  explainerText?: string;
  /** Error text shown when invalid */
  errorText?: string;
  /** Whether the field is in an invalid/error state (shows red border + error icon) */
  invalid?: boolean;
  /** Accessibility text to denote invalid state */
  invalidText?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Accessibility text to denote required state */
  requiredText?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Ref forwarded to the root element for measurement/focus */
  ref?: React.Ref<BoxRef>;
  /** Ref forwarded to the focusable trigger button (e.g. to move focus to it on a validation error) */
  buttonRef?: React.Ref<BoxRef>;
  /** Test ID for the component */
  testID?: string;
  /** Defines the padding style overrides. */
  sx?: ViewStyle;
  /**
   * When true, the selected value truncates to a single line instead of pushing
   * the chevron out of the field at large text sizes. See RN-9687.
   */
  truncateLabel?: boolean;
};

export function Select<ValueT>({
  value,
  onChange,
  items,
  placeholder,
  labelText,
  ariaLabel,
  explainerText,
  errorText,
  invalid = false,
  invalidText,
  required = false,
  requiredText,
  disabled = false,
  buttonRef,
  testID,
  sx,
  truncateLabel = false,
}: SelectProps<ValueT>) {
  // When invalid is true but no errorText provided, use a space to trigger
  // the red border state in SelectInput (MADS limitation: also shows error icon)
  const effectiveErrorText = invalid && !errorText ? " " : errorText;
  const [isOpen, setIsOpen] = useState(false);
  const focusedOptionRef = useRef<BoxRef>(null);
  const theme = useTheme();

  // Stable id linking the combobox trigger to its listbox via aria-controls.
  const listboxId = `MADS_Select_listbox_${useId()}`;
  const selectedValueDescriptionId = `MADS_Select_value_${useId()}`;

  const triggerRef = useRef<ComponentRef<typeof Box>>(null);
  const selectButtonRef = useRef<BoxRef>(null);
  // Keep the internal ref (used for focus restore) while also forwarding the trigger to the caller.
  const setSelectButtonRef = useCallback(
    (node: BoxRef | null) => {
      selectButtonRef.current = node;
      if (typeof buttonRef === "function") {
        buttonRef(node);
      } else if (buttonRef) {
        buttonRef.current = node;
      }
    },
    [buttonRef],
  );
  const dropdownRef = useRef<ComponentRef<typeof Box>>(null);
  const restoreFocusAfterCloseRef = useRef(false);
  const [dropdownRect, setDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);

  // Measure trigger position to anchor the portal dropdown below it
  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownRect(null);
      setPortalHost(null);
      return;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const el = triggerRef.current as unknown as HTMLElement | null;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownRect({ top: rect.bottom, left: rect.left, width: rect.width });

    // Keep the dropdown inside react-native-web Modal's focus-trap subtree.
    // Portaling to document.body causes that trap to immediately redirect
    // option focus back into the modal. The dialog's parent overlay is inside
    // the trap but outside the dialog's clipped/scrolling content.
    setPortalHost(el.closest('[role="dialog"]')?.parentElement ?? document.body);
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  // SelectInput exposes its focusable SelectButton through SelectButtonProps.
  // Use that stable ref rather than coupling focus management to its ARIA role.
  const getTriggerElement = useCallback((): HTMLElement | null => {
    const element = selectButtonRef.current;
    return element instanceof HTMLElement ? element : null;
  }, []);

  // Find the label for the currently selected value
  const selectedLabel = useMemo(() => {
    const selectedItem = items.find((item) => item.value === value);
    return selectedItem?.label;
  }, [items, value]);

  // RN-7518: MADS SelectInput cannot name a trigger from an external label.
  // Apply the web-only name to its forwarded DOM node, following the same
  // shared-layer pattern used by PickerCombobox.web.tsx.
  // TODO(RN-7518): replace this with an ariaLabel prop on MADS SelectInput.
  useEffect(() => {
    const triggerElement = getTriggerElement();
    if (!triggerElement || !ariaLabel) return;

    triggerElement.setAttribute("aria-label", ariaLabel);
    return () => triggerElement.removeAttribute("aria-label");
  }, [ariaLabel, getTriggerElement]);

  // Keep the field name stable while exposing its selected value separately.
  // Preserve helper/error descriptions that MADS already attached.
  useEffect(() => {
    const triggerElement = getTriggerElement();
    if (!triggerElement || !ariaLabel || !selectedLabel) return;

    const describedByIds = new Set(
      (triggerElement.getAttribute("aria-describedby") ?? "").split(/\s+/).filter(Boolean),
    );
    describedByIds.add(selectedValueDescriptionId);
    triggerElement.setAttribute("aria-describedby", Array.from(describedByIds).join(" "));

    return () => {
      const currentIds = new Set(
        (triggerElement.getAttribute("aria-describedby") ?? "").split(/\s+/).filter(Boolean),
      );
      currentIds.delete(selectedValueDescriptionId);
      if (currentIds.size > 0) {
        triggerElement.setAttribute("aria-describedby", Array.from(currentIds).join(" "));
      } else {
        triggerElement.removeAttribute("aria-describedby");
      }
    };
  }, [
    ariaLabel,
    effectiveErrorText,
    explainerText,
    getTriggerElement,
    selectedLabel,
    selectedValueDescriptionId,
  ]);

  // Return DOM focus to the trigger so keyboard users aren't stranded once the
  // portaled list unmounts.
  const focusTrigger = useCallback(() => {
    getTriggerElement()?.focus?.();
  }, [getTriggerElement]);

  // Restore focus only after React has committed the selected value and removed
  // the portaled list. Focusing synchronously while the option is still mounted
  // can be lost when that focused option is removed.
  useLayoutEffect(() => {
    if (isOpen || !restoreFocusAfterCloseRef.current) return;

    restoreFocusAfterCloseRef.current = false;
    focusTrigger();
  }, [isOpen, focusTrigger]);

  // Move focus to the field before/after the trigger in tab order, mirroring a
  // native <select>: dismissing the open list with Tab advances the form rather
  // than dropping focus into the detached portal. The portal's options sort
  // after the form in document order, so the trigger's tab-order neighbour is
  // the adjacent field; the dropdown subtree is excluded as a safeguard.
  const advanceFocusFromTrigger = useCallback(
    (backwards: boolean) => {
      const combobox = getTriggerElement();
      if (!combobox || typeof document === "undefined") {
        combobox?.focus?.();
        return;
      }
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const dropdownEl = dropdownRef.current as unknown as HTMLElement | null;
      const focusables = Array.from(
        document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el === combobox || !dropdownEl?.contains(el));
      const index = focusables.indexOf(combobox);
      const next = index === -1 ? null : focusables[index + (backwards ? -1 : 1)];
      (next ?? combobox).focus?.();
    },
    [getTriggerElement],
  );

  // While closed, open the list with ArrowDown/ArrowUp/Enter/Space so the
  // trigger behaves like a WAI-ARIA combobox (not just a click target).
  useEffect(() => {
    if (isOpen || typeof document === "undefined") return;
    const combobox = getTriggerElement();
    if (!combobox) return;
    const handleTriggerKeyDown = (e: KeyboardEvent) => {
      if (["ArrowDown", "ArrowUp", "Enter", " ", "Spacebar"].includes(e.key)) {
        e.preventDefault();
        setIsOpen(true);
      }
    };
    combobox.addEventListener("keydown", handleTriggerKeyDown);
    return () => combobox.removeEventListener("keydown", handleTriggerKeyDown);
  }, [isOpen, getTriggerElement]);

  // Close when clicking outside the trigger/dropdown, or on Escape/Tab. Escape
  // restores focus to the trigger; Tab dismisses and advances the form.
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const triggerEl = triggerRef.current as unknown as HTMLElement | null;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const dropdownEl = dropdownRef.current as unknown as HTMLElement | null;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      if (triggerEl?.contains(e.target as Node)) return;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      if (dropdownEl?.contains(e.target as Node)) return;
      handleClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        restoreFocusAfterCloseRef.current = true;
        handleClose();
      } else if (e.key === "Tab") {
        e.preventDefault();
        handleClose();
        advanceFocusFromTrigger(e.shiftKey);
      }
    };
    // The portaled list is fixed-positioned and anchored to the trigger only
    // at open time, so scrolling the page/form would detach it from the
    // input. Mirror a native <select> and dismiss on scroll — but ignore
    // scrolls that originate inside the open list (its own option scrolling).
    const handleScroll = (e: Event) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const dropdownEl = dropdownRef.current as unknown as HTMLElement | null;
      if (dropdownEl && e.target instanceof Node && dropdownEl.contains(e.target)) return;
      handleClose();
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    // Capture phase: scroll events don't bubble, so this is required to catch
    // scrolling on any ancestor scroll container (e.g. the modal's ScrollView).
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, focusTrigger, advanceFocusFromTrigger]);

  useEffect(() => {
    if (!isOpen || !dropdownRect) return;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const node = focusedOptionRef.current as unknown as { focus?: () => void } | null;
    node?.focus?.();
  }, [isOpen, dropdownRect]);

  useEffect(() => {
    if (!isOpen || !dropdownRect) return;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    uninertOwnPortal(dropdownRef.current as unknown as HTMLElement | null);
  }, [isOpen, dropdownRect]);

  // Build items for SelectList
  const selectListItems = useMemo(() => {
    return items.map((item, index) => {
      const isSelected = item.value === value;
      const shouldFocus = isSelected || (index === 0 && value === undefined);

      return {
        text: item.label,
        selected: isSelected,
        onPress: () => {
          onChange(item.value);
          restoreFocusAfterCloseRef.current = true;
          handleClose();
        },
        ref: shouldFocus ? focusedOptionRef : undefined,
      };
    });
  }, [items, value, onChange]);

  return (
    <Box ref={triggerRef} sx={sx} testID={testID}>
      <SelectInput
        text={selectedLabel}
        placeholderText={placeholder}
        labelText={labelText}
        explainerText={explainerText}
        errorText={effectiveErrorText}
        invalidText={invalidText}
        required={required}
        requiredText={requiredText}
        disabled={disabled}
        expanded={isOpen}
        truncateLabel={truncateLabel}
        ariaControls={listboxId}
        onPress={() => setIsOpen(true)}
        SelectButtonProps={{ ref: setSelectButtonRef }}
      />
      {ariaLabel && selectedLabel && (
        <Box id={selectedValueDescriptionId} sx={visuallyHiddenValueStyle}>
          <Text>{selectedLabel}</Text>
        </Box>
      )}

      {isOpen &&
        dropdownRect &&
        portalHost &&
        createPortal(
          <Box
            ref={dropdownRef}
            id={listboxId}
            sx={{
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
              position: "fixed" as never,
              top: dropdownRect.top,
              left: dropdownRect.left,
              width: dropdownRect.width,
              zIndex: 9999,
              maxHeight: Math.min(300, window.innerHeight - dropdownRect.top - 20),
              // Clip to the rounded corners; the inner SelectList
              // (scrollEnabled) is the scroller so the option list
              // scrolls within maxHeight instead of getting cut off.
              overflow: "hidden",
              backgroundColor: theme.colors.background,
              borderRadius: theme.borderRadius.lg,
              boxShadow: theme.shadows.medium,
              paddingBottom: theme.spacing.s2,
            }}
            testID={testID ? `${testID}-modal` : undefined}
          >
            <SelectList
              items={selectListItems}
              scrollEnabled
              testID={testID ? `${testID}-list` : undefined}
            />
          </Box>,
          portalHost,
        )}
    </Box>
  );
}

export default Select;
